import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import type { LlmChatAttachmentDto } from '@paladin-monorepo/common';
import { llmChatApi } from '@/lib/api/llm-chat';
import { cn } from '@/lib/utils';

interface LlmChatMessageAttachmentProps {
  sessionId: string;
  attachment: LlmChatAttachmentDto;
  className?: string;
}

export function LlmChatMessageAttachment({
  sessionId,
  attachment,
  className,
}: LlmChatMessageAttachmentProps) {
  const { t } = useTranslation();
  const [isOpening, setIsOpening] = useState(false);

  const handleOpen = useCallback(async () => {
    if (isOpening) return;
    setIsOpening(true);
    try {
      await llmChatApi.openOrDownloadAttachment(
        sessionId,
        attachment.id,
        attachment.originalName,
        attachment.mimeType,
      );
    } catch {
      toast.error(t('llmChat.attachmentOpenError'));
    } finally {
      setIsOpening(false);
    }
  }, [attachment, isOpening, sessionId, t]);

  return (
    <button
      type="button"
      onClick={() => void handleOpen()}
      disabled={isOpening}
      className={cn(
        'flex max-w-full items-center gap-1.5 text-xs underline-offset-2 hover:underline disabled:opacity-60',
        className,
      )}
      title={attachment.originalName}
    >
      <Paperclip className="h-3 w-3 shrink-0" />
      <span className="truncate">{attachment.originalName}</span>
    </button>
  );
}
